import {
  LICENCIA_ESTADO,
  LICENSES_ALL_TYPES,
  LICENSES_STATUS,
  LICENSES_TYPES,
  LICENSES_TYPES_NAME_TABLE,
  MANUAL_LICENSE_TYPE_OTHERS,
  MANUAL_LICENSE_TYPES_NAMES,
  MEDICAL_LICENSES_TYPES,
  TIME_TO_REQUEST_MEDICAL_EXTENSION,
  TYPE_INSTRUCTIVE,
} from '@licencias/utils/constants';
import {
  buildQueryString,
  isAllSelected,
  normalizedString,
} from '@utils/Functions';
import { v4 as uuidv4 } from 'uuid';
import theme from '@theme';
import dayjs from 'dayjs';

export const esLicenciaPorDonacionDeSangre = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === MEDICAL_LICENSES_TYPES.donacion_sangre;
};

export const esLicenciaMedica = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return Object.keys(MEDICAL_LICENSES_TYPES).includes(type);
};

export const esLicenciaPorVacaciones = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.vacaciones;
};

export const esLicenciaPorEnfermedad = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.medical;
};

export const esLicenciaPorEnfermedadFamiliar = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.enfermedad_familiar;
};

export const esLicenciaPorExamen = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.examen;
};

export const esLicenciaPorMotivosPersonales = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.tramites;
};

export const esLicenciaPorART = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === LICENSES_TYPES.art;
};

export const esLicenciaOtras = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return (
    type === LICENSES_TYPES.mudanza ||
    type === LICENSES_TYPES.maternidad ||
    type === LICENSES_TYPES.matrimonio ||
    type === LICENSES_TYPES.paternidad ||
    type === LICENSES_TYPES.excedencia ||
    type === LICENSES_TYPES.sin_goce_de_sueldo ||
    type === LICENSES_TYPES.citacion_judicial ||
    type === LICENSES_TYPES.fallecimiento_familiar ||
    type === LICENSES_TYPES.deportiva ||
    type === LICENSES_TYPES.gremial ||
    type === LICENSES_TYPES.religiosa ||
    type === LICENSES_TYPES.bomberos ||
    type === LICENSES_TYPES.otras
  );
};

export const esLicenciaInstructivo = (licenseOrType) => {
  const type = licenseOrType?.type || licenseOrType;
  return type === TYPE_INSTRUCTIVE;
};

export function canRequestMedicalExtension(license) {
  const esEnfermedad =
    esLicenciaPorEnfermedad(license) ||
    esLicenciaPorEnfermedadFamiliar(license);

  const isJustified = license?.status_process === LICENCIA_ESTADO.JUSTIFICADA;
  let isTimeAvailable = false;

  if (license?.to_date) {
    const toDate = dayjs
      .utc(license.to_date)
      .tz('America/Argentina/Buenos_Aires')
      .add(TIME_TO_REQUEST_MEDICAL_EXTENSION, 'hour');
    const endTime = dayjs();
    isTimeAvailable = !endTime.isAfter(toDate);
  }

  return esEnfermedad && isJustified && isTimeAvailable;
}

export function canSendMedicalMessage(license) {
  return (
    (esLicenciaPorEnfermedad(license) ||
      esLicenciaPorEnfermedadFamiliar(license)) &&
    (LICENSES_STATUS[license?.status_process] ===
      LICENSES_STATUS.extension_en_evaluacion ||
      LICENSES_STATUS[license?.status_process] ===
        LICENSES_STATUS.document_loaded)
  );
}

export function canSendMessage(license) {
  return (
    esLicenciaOtras(license) &&
    LICENSES_STATUS?.[license?.status_process] != LICENSES_STATUS.cancelled
  );
}

export const isManualLicense = ({ manual }) => {
  return manual || false;
};

export const buildNameOfTypeManualLicense = (license) => {
  const { type, title, notes } = license;
  const typeLicense = type || title;
  if (isManualLicense(license)) {
    if (isTypeOthers(typeLicense)) return notes;
    return MANUAL_LICENSE_TYPES_NAMES[typeLicense];
  }
  return LICENSES_TYPES_NAME_TABLE[typeLicense];
};

export const isTypeOthers = (licenseType) => {
  return licenseType === MANUAL_LICENSE_TYPE_OTHERS;
};

export const convertLicenseType = (license) => {
  let licenseWithModality = license?.type;
  if (license?.modality) licenseWithModality += `_${license?.modality}`;
  return licenseWithModality;
};

export const esFeriado = (feriados, date) => {
  const holidayDates = feriados?.map(
    (feriado) => new Date(feriado.date).toISOString().split('T')[0],
  );
  const formattedDate = new Date(date).toISOString().split('T')[0];
  return holidayDates?.includes(formattedDate);
};

export const highlightMatches = (str1, str2) => {
  if (!str1 || !str2) return str2;
  const normalizedStr1 = normalizedString(str1);
  const normalizedStr2 = normalizedString(str2);

  const positions = [];
  const highlightedSlotName = [];

  normalizedStr2.split('').forEach((_, i) => {
    const substring = normalizedStr2.substr(i, str1.length);
    if (substring === normalizedStr1) {
      for (let j = i; j < i + str1.length; j++) {
        positions.push(j);
      }
    }
  });

  const strMap = str2.split('');
  const strMapWithIds =
    strMap?.map((letter) => ({ char: letter, uuid: uuidv4() })) || [];

  strMapWithIds.forEach((charWithId, i) => {
    const isHighlighted = positions.includes(i);
    highlightedSlotName.push(
      <span
        key={charWithId.uuid}
        style={{
          backgroundColor: isHighlighted
            ? theme.palette.common.yellowSearch
            : '',
        }}
      >
        {charWithId.char}
      </span>,
    );
  });

  return highlightedSlotName;
};

const setFilter = (filters, key, value, condition = true) => {
  if (condition) filters[key] = value;
};

const hasItems = (arr) => Array.isArray(arr) && arr.length > 0;

export const getTableFilters = (params = {}, sort = false) => {
  const {
    q,
    types,
    page_size,
    page_num,
    status_process,
    categoria_medica,
    sort_field,
    sort_order,
    vista,
    active,
    from_date,
    to_date,
    fecha_desde,
    fecha_hasta,
    estado,
    tags,
    idLicense,
    idUserCompartido,
    idRelatedUser,
  } = params;

  const filters = {};

  setFilter(filters, 'q', q?.trim(), !!q?.trim());
  setFilter(filters, 'vista', vista, !!vista);
  setFilter(filters, 'page_size', page_size, !!page_size);
  setFilter(filters, 'page_num', page_num, !!page_num);
  setFilter(filters, 'active', active, active !== undefined);

  setFilter(
    filters,
    'from_date',
    from_date || fecha_desde,
    !!(from_date || fecha_desde),
  );
  setFilter(
    filters,
    'to_date',
    to_date || fecha_hasta,
    !!(to_date || fecha_hasta),
  );

  setFilter(filters, 'id_license_excluded', idLicense, !!idLicense);
  setFilter(
    filters,
    'id_user_compartido',
    idUserCompartido,
    !!idUserCompartido,
  );
  setFilter(filters, 'id_related_user', idRelatedUser, !!idRelatedUser);

  setFilter(filters, 'etiquetas', tags, hasItems(tags));

  setFilter(
    filters,
    'type',
    types,
    hasItems(types) && !isAllSelected(types, LICENSES_ALL_TYPES),
  );

  setFilter(
    filters,
    'status_process',
    status_process,
    hasItems(status_process) && !isAllSelected(status_process, LICENSES_STATUS),
  );

  setFilter(filters, 'status_process', estado, !!estado);

  setFilter(
    filters,
    'categoria_medica',
    categoria_medica,
    !!categoria_medica && categoria_medica !== 'all',
  );

  if (sort) {
    filters.sort_field = sort_field || 'date_created';
    filters.sort_order = sort_order || '-1';
  }

  return buildQueryString(filters);
};
